"use client";

import { useEffect, useState } from "react";

import Image from "next/image";
import { useRouter } from "next/navigation";

import { ROUTES } from "@unreserved/constants";
import { useToast } from "@unreserved/hooks/use-toast";
import { Info } from "lucide-react";

import { Button } from "@/app/_components/shared/button";
import { AddressSearchInput } from "@/app/_components/shared/search-input";
import { HERO_DATA, HERO_NOTIFICATIONS_DATA } from "@/app/_libs/data/landing-page/hero-section";
import type { PropertyAddressSuggestionsDto } from "@/app/_libs/types/property-estimates";
import { usePropertyPriceEstimate } from "@/hooks/use-listing";
import { cn } from "@/lib/utils";
import { useGetEstimatesStore } from "@/stores/get-estimates-store";

export function HomeValuationSection() {
  const router = useRouter();
  const { toast } = useToast();
  const [selectedProperty, setSelectedProperty] = useState<PropertyAddressSuggestionsDto | null>(
    null,
  );
  const [visibleNotifications, setVisibleNotifications] = useState<boolean[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { refetch } = usePropertyPriceEstimate(selectedProperty?.id || "", false);
  const {
    setSelectedProperty: setStoreSelectedProperty,
    setPropertyData: setStorePropertyData,
    setCurrentStep: setStoreCurrentStep,
    setCameFromLandingPage: setStoreCameFromLandingPage,
  } = useGetEstimatesStore();

  useEffect(() => {
    const timers = HERO_NOTIFICATIONS_DATA.map((_, index) => {
      return setTimeout(() => {
        setVisibleNotifications((prev) => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, index * 200);
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const handleGetEstimate = async () => {
    if (!selectedProperty) return;

    setIsLoading(true);
    try {
      const result = await refetch();
      if (result.data?.status === "Success" && result.data?.data) {
        // Here we are storing data in store so that we can use it in the get-estimate flow steps
        setStoreSelectedProperty(selectedProperty);
        setStorePropertyData(result.data.data);
        setStoreCurrentStep(2);
        setStoreCameFromLandingPage(true);
        router.push(ROUTES.UNAUTHENTICATED.GET_ESTIMATES);
      } else {
        toast.error("Failed to fetch property estimate. Please try again.");
      }
    } catch (error) {
      console.error("Failed to fetch property estimate:", error);
      toast.error("Failed to fetch property estimate. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="bg-background-0 relative flex w-full flex-col items-start overflow-hidden pt-4 pb-4 sm:pt-5 sm:pb-5 md:pt-6 md:pb-10 lg:pt-4 lg:pb-10">
      <div className="relative mx-auto flex w-full flex-col px-[6%] md:flex-row">
        <div className="relative mt-6 flex w-full flex-col gap-4 sm:gap-5 md:mt-16 md:w-[55%] md:gap-6 lg:mt-0 lg:h-[502px] lg:w-[60%] lg:justify-end lg:gap-[20%]">
          <div className="item-center relative flex flex-col gap-2.5 sm:gap-3 md:items-start md:gap-2.5">
            <div className="relative flex flex-col gap-1.5 font-bold sm:gap-2 md:items-start md:gap-1">
              <h1 className="text-typography-900 relative text-2xl leading-[1.1] sm:leading-tight md:text-3xl lg:text-5xl">
                {HERO_DATA.primaryTitle}
              </h1>
              <h1 className="text-primary-700 relative w-full text-2xl leading-[1.1] sm:leading-tight md:text-3xl lg:text-5xl">
                {HERO_DATA.secondaryTitle}
              </h1>
            </div>
            <p className="text-typography-800 relative w-full text-sm leading-relaxed font-normal md:w-[70%] md:text-base lg:text-xl">
              {HERO_DATA.description}
            </p>
          </div>
          <div className="relative flex w-full flex-col items-start gap-3 md:gap-4 lg:gap-6">
            <div className="relative flex h-12 w-full max-w-[100%] items-start gap-2 rounded-full sm:h-12 sm:gap-2.5 md:max-w-[90%] lg:max-w-[75%] xl:max-w-[60%]">
              <div
                className="bg-background-0 border-border-300 relative w-full flex-1 rounded-full border"
                style={{ overflow: "visible" }}>
                <div className="relative flex w-full flex-row flex-nowrap items-center gap-2 rounded-full py-1.5 pr-2 pl-3 sm:gap-2 sm:pr-3 md:py-2.5 lg:pr-4 lg:pl-5 xl:pr-2 xl:pl-3">
                  <AddressSearchInput onPropertyChange={setSelectedProperty} />
                  <Button
                    disabled={!selectedProperty || isLoading}
                    onClick={handleGetEstimate}
                    className={cn(
                      "bg-primary-700 text-typography-0 xs:px-4 hover:bg-primary-700/90 relative h-9 shrink-0 cursor-pointer items-center justify-center rounded-full px-3 py-0 text-xs transition-all duration-300 sm:px-5 lg:h-10 lg:text-base",
                      (!selectedProperty || isLoading) && "cursor-not-allowed opacity-50",
                    )}>
                    {isLoading ? "Loading..." : HERO_DATA.buttonLabel}
                  </Button>
                </div>
              </div>
            </div>
            <div className="relative flex w-full items-start gap-1.5">
              <Info className="mt-0.3 text-border-600 size-4 lg:mt-0.5" />
              <p className="text-typography-500 text-[11px] font-normal lg:text-sm">
                {HERO_DATA.helperText}
              </p>
            </div>
          </div>
        </div>

        <div className="xs:h-[440px] relative mx-auto mt-5 h-[420px] w-full max-w-[480px] overflow-hidden rounded-t-full sm:mt-6 sm:h-[460px] md:right-5 md:mt-6 md:h-[440px] md:w-[42%] md:max-w-none lg:mt-8 lg:h-[560px] xl:h-[680px]">
          <div className="absolute inset-0">
            <div className="relative h-full w-full">
              <Image
                src="/images/property-list3.png"
                alt="Real Estate Architecture"
                fill
                className="pointer-events-none object-cover object-top md:object-center"
                sizes="(max-width: 768px) 100vw, 42vw"
                priority
                fetchPriority="high"
              />
            </div>
          </div>

          {HERO_NOTIFICATIONS_DATA.map((notification, index) => {
            const isVisible = visibleNotifications[index];
            const isRightAligned = notification.containerClass.includes("right");
            const slideAnimationClass = isRightAligned
              ? "animate-hero-notification-curtain"
              : "animate-hero-notification-slide-in";

            return (
              <div
                key={index}
                className={cn(
                  "bg-background-950 absolute flex origin-center",
                  notification.containerClass,
                  isVisible
                    ? `opacity-100 ${slideAnimationClass}`
                    : isRightAligned
                      ? "scale-85 opacity-0"
                      : "-translate-x-[150px] scale-85 opacity-0",
                )}>
                <Image
                  src={notification.imageSrc}
                  alt={notification.alt}
                  width={24}
                  height={24}
                  className="h-6 w-6 shrink-0 rounded-full lg:h-10 lg:w-10"
                />
                <p
                  className={cn(
                    "text-typography-0 max-w-full font-semibold whitespace-normal",
                    notification.textClass,
                    isVisible &&
                      "animate-hero-notification-text-curtain inline-block origin-center",
                  )}>
                  {notification.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
